import { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { message, Modal, Popconfirm, Switch, Table, TableProps } from "antd";
import * as Yup from "yup";
import { useFormik } from "formik";
import { OfficialServiceDataType } from "../../interfaces/admin.interface";
import ErrorMessage from "../../components/shared/ErrorMessage/ErrorMessage";
import MainButton from "../../components/shared/MainButton/MainButton";
import Spinner from "../../components/shared/Spinner/Spinner";
import ShowImage from "../../components/shared/show-image/ShowImage";
import SelectImage from "../../components/shared/select-image/SelectImage";
import { FaPlus } from "react-icons/fa";
import { OfficialServiceService } from "../../services/official_service.service";

const OfficialService = () => {
  const [partners, setPartners] = useState<OfficialServiceDataType[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [editingRecord, setEditingRecord] = useState<OfficialServiceDataType | null>(
    null
  );
  const [logoFileList, setLogoFileList] = useState<any[]>([]);
  const [showImageModal, setShowImageModal] = useState<boolean>(false);
  const [selectedImage, setSelectedImage] = useState<string>("");

  useEffect(() => {
    setLoading(true);
    OfficialServiceService.getAllServices()
      .then((response) => {
        if (Array.isArray(response?.data?.data)) {
          const dataWithKeys = response?.data?.data?.map(
            (item: OfficialServiceDataType, index: number) => ({
              ...item,
              key: index + 1,
            })
          );
          setPartners(dataWithKeys);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);
  const handleAfterSubmit = async () => {
    setLoading(true);
    setIsModalVisible(false);
    resetForm();
    const updatedData = await OfficialServiceService.getAllServices();
    setPartners(
      updatedData?.data?.data?.map((item: OfficialServiceDataType, index: number) => ({
        ...item,
        key: index + 1,
      }))
    );
  };
  const {
    handleSubmit,
    handleChange,
    values,
    resetForm,
    setFieldValue,
    touched,
    errors,
  } = useFormik({
    initialValues: {
      name: "",
      logo: null,
      is_visible: false,
    },
    validationSchema: Yup.object({
      name: Yup.string().required("Rəsmi servis mərkəzinin adı tələb olunur"),
      logo: Yup.string().required("Rəsmi servis mərkəzinin logosu tələb olunur"),
    }),
    onSubmit: async (values) => {
      if (isEditing && editingRecord) {
        const id = editingRecord.id;
        const isEqual =
          editingRecord.name === values.name &&
          editingRecord.logo === values.logo &&
          editingRecord.is_visible === values.is_visible;
        if (isEqual) {
          message.info("Dəyişiklik edilmədi.");
        } else {
          if (editingRecord.logo === values.logo) {
            const payload = {
              name: values.name,
              is_visible: values.is_visible,
            };
            const response = await OfficialServiceService.updateService({
              ...payload,
              id,
            });
            if (response.status === 200) {
              message.success("Rəsmi servis mərkəzi uğurla yeniləndi!");
              await handleAfterSubmit();
            } else {
              message.error(
                response.errorMessage || "Rəsmi servis mərkəzini yeniləmək alınmadı."
              );
            }
          } else {
            const response = await OfficialServiceService.updateService({
              ...values,
              id,
            });
            if (response.status === 200) {
              message.success("Rəsmi servis mərkəzi uğurla yeniləndi!");
              await handleAfterSubmit();
            } else {
              message.error(
                response.errorMessage || "Rəsmi servis mərkəzini yeniləmək alınmadı."
              );
            }
          }
        }
      } else {
        const response = await OfficialServiceService.addPService(values);
        if (response.status === 201) {
          message.success("Rəsmi servis mərkəzi uğurla əlavə edildi!");
          await handleAfterSubmit();
        } else {
          message.error(
            response.errorMessage || "Rəsmi servis mərkəzini əlavə etmək alınmadı."
          );
        }
      }
      setLoading(false);
    },
  });

  const handleSwitchChange = async (checked: boolean, recordId: number) => {
    OfficialServiceService.updateService({
      id: recordId,
      is_visible: checked,
    });
    if (checked) {
      message.success("Rəsmi servis mərkəzi artıq saytda görünür.");
    } else {
      message.info("Rəsmi servis mərkəzi artıq saytda görünmür.");
    }
    setPartners((prevData) =>
      prevData.map((item) =>
        item.id === recordId ? { ...item, is_visible: checked } : item
      )
    );
  };

  const handleDeleteClick = async (record: OfficialServiceDataType) => {
    try {
      await OfficialServiceService.deleteService(record.id.toString());
      setPartners((prevData) =>
        prevData.filter((item) => item.id !== record.id)
      );
      message.success("Rəsmi servis mərkəzi uğurla silindi!");
    } catch (error) {
      message.error("Rəsmi servis mərkəzini silmək alınmadı.");
    }
  };
  const columns: TableProps<OfficialServiceDataType>["columns"] = [
    {
      title: "№",
      dataIndex: "key",
      sorter: (a, b) => a.key - b.key,
    },
    {
      title: "Ad",
      dataIndex: "name",
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: "Logo",
      dataIndex: "logo",
      render: (_, record) => (
        <div
          style={{ cursor: "pointer" }}
          onClick={() => {
            setSelectedImage(record.logo!);
            setShowImageModal(true);
          }}
        >
          {record.logo ? (
            <img
              style={{ width: "100px" }}
              src={record.logo}
              alt={`${record.name} logo`}
            />
          ) : null}
        </div>
      ),
    },
    {
      title: "Saytda görünürlük",
      dataIndex: "is_visible",
      render: (_, record) => (
        <div className="switch_input">
          <Switch
            checked={record.is_visible}
            onChange={(checked) => handleSwitchChange(checked, record.id)}
          />
        </div>
      ),
    },
    {
      title: (
        <div className="settings">
          <i className="fa-solid fa-gear"></i>
        </div>
      ),
      key: "action",
      render: (_, record) => (
        <div className="settings_icons">
          <div className="edit" onClick={() => handleEditClick(record)}>
            <i className="fa-solid fa-pen-to-square"></i>
          </div>
          <Popconfirm
            title="Bu rəsmi servis mərkəzini siləcəyinizə əminsiniz?"
            onConfirm={() => handleDeleteClick(record)}
            onCancel={() => message.info("Silinmə əməliyyatı ləğv edildi.")}
            okText="Bəli"
            cancelText="Xeyr"
            placement="topRight"
          >
            <div className="delete">
              <i className="fa-solid fa-trash"></i>
            </div>
          </Popconfirm>
        </div>
      ),
    },
  ];

  const handleAddClick = () => {
    setIsEditing(false);
    setEditingRecord(null);
    setIsModalVisible(true);
    resetForm();
    setFieldValue("logo", null);
    setLogoFileList([]);
  };
  const handleEditClick = (record: OfficialServiceDataType) => {
    setIsEditing(true);
    setEditingRecord(record);
    setIsModalVisible(true);
    setFieldValue("name", record.name);
    setFieldValue("logo", record.logo);
    setFieldValue("is_visible", record.is_visible);
    setLogoFileList(
      record.logo
        ? [
            {
              uid: "-1",
              name: `${record.name} logo`,
              status: "done",
              url: record.logo,
            },
          ]
        : []
    );
  };

  return (
    <div id="pages">
      <Helmet>
        <title>Orhan Door | Rəsmi Servis Mərkəzi</title>
      </Helmet>
      {loading ? (
        <Spinner />
      ) : (
        <div>
          <div className="page_head">
            <h3 className="page_title">Rəsmi Servis Mərkəzi</h3>
          </div>
          <div className="pages_components">
            <MainButton onClick={handleAddClick}>
              <FaPlus size={20} />
            </MainButton>
            <div className="pages_table">
              <Table<OfficialServiceDataType>
                scroll={{ x: "100%" }}
                columns={columns}
                dataSource={partners}
                rowKey={(record) => record.id.toString()}
                pagination={false}
              />
            </div>
          </div>
        </div>
      )}

      <Modal
        title={
          isEditing ? "Rəsmi Servis Mərkəzini redaktə edin" : "Yeni Rəsmi Servis Mərkəzi əlavə edin"
        }
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={null}
        style={{ minWidth: "340px" }}
        centered
      >
        <form className="modal_form" onSubmit={handleSubmit}>
          <div className="form_input">
            <label className="label" htmlFor="name">
            Rəsmi Servis Mərkəzinin adı
            </label>
            <div className="input_wrapper">
              <input
                name="name"
                id="name"
                placeholder="Rəsmi Servis Mərkəzinin adı"
                value={values.name}
                onChange={handleChange}
              />
              <span className="focus_border"></span>
            </div>
            <ErrorMessage error={errors.name} touched={touched.name} />
          </div>
          <div className="form_input">
            <SelectImage
              image_name="Şəkil seçin"
              logoFileList={logoFileList}
              setLogoFileList={setLogoFileList}
              field="logo"
              setFieldValue={setFieldValue}
            />
            <ErrorMessage error={errors.logo} touched={touched.logo} />
          </div>
          <div className="form_input">
            <label className="active_label" htmlFor="">
            Rəsmi Servis Mərkəzinin saytda görünməsi
            </label>
            <Switch
              checked={values.is_visible}
              onChange={(checked) => setFieldValue("is_visible", checked)}
            />
          </div>
          <div>
            <MainButton full type="submit">
              {isEditing ? "Redaktə edin" : "Əlavə edin"}
            </MainButton>
          </div>
        </form>
      </Modal>
      <ShowImage
        showImageModal={showImageModal}
        setShowImagesModal={setShowImageModal}
        image={selectedImage}
      />
    </div>
  );
};

export default OfficialService;
